// import countries from './data/countries';
// import setCookie, { defaultExpiration } from './utils/setCookie';
import getCookie from './utils/getCookie';

/**
 * A custom element for enhancing a static BBB my account link to a dialog window
 * that shows users their currently logged in information
 *
 * @module iabbb-my-account
 */

const userTypes = {
  business: 'business',
  consumer: 'consumer',
  both: 'both',
};

class MyAccount extends HTMLElement {
  constructor() {
    super();

    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });
    this.useRedesign = window.headerFooterData.isMyBbbRedesignEnabled || params.useMenuRedesign === 'true';

    this.render = () => {
      this.setButtonOnClick();
      try {
        this.initData();
      } catch {
        this.user = null;
      }
      this.updateButtonLabel();
      clearInterval(this.cookieInterval);
      this.cookieInterval = window.setInterval(this.checkAuthState, 5000);
    };
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    clearInterval(this.cookieInterval);
  }

  checkAuthState = () => {
    const isLoggedIn = getCookie('iabbb_auth_login_hint') === 'true';

    if (!this.user && isLoggedIn) {
      this.initData();
    } else if (this.user && !isLoggedIn) {
      this.user = null;
    }
    this.updateButtonLabel();
  };

  initData = () => {
    if (this.user) {
      return;
    }

    if (getCookie('iabbb_auth_login_hint') !== 'true') {
      this.user = null;
      return;
    }

    const userTypeCode = getCookie('iabbb_auth_user_type');
    let userType;
    if (userTypeCode === 'b') {
      userType = userTypes.business;
    } else if (userTypeCode === 'bc') {
      userType = userTypes.both;
    } else {
      userType = userTypes.consumer;
    }

    this.user = { userType };
  };

  buildAndOpenRedesignDialog = () => {
    const closeIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em"><path d="M7.5 105c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l151 151 151-151c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-151 151 151 151c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-151-151-151 151c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l151-151-151-151z"/></svg>`;
    const visuallyHiddenLabel = `<span class="visually-hidden">close account dialog</span>`;

    const closeButton = document.createElement('button');
    closeButton.className = 'account-close-button bds-button-unstyled';
    closeButton.innerHTML = `${closeIcon}${visuallyHiddenLabel}`;

    const dialog = document.createElement('dialog');
    dialog.className = 'account-dialog-redesign';
    dialog.setAttribute('data-has-user', this.user ? 'true' : 'false');

    closeButton.onclick = () => {
      this.destroyRedesignDialog();
      this.querySelector('button').focus();
    };

    dialog.appendChild(closeButton);

    const chevronRightIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em"><path d="M311.1 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L243.2 256 73.9 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/></svg>`;

    if (this.user) {
      const loggedInSection = document.createElement('div');
      loggedInSection.className = 'account-links-section';
      dialog.appendChild(loggedInSection);

      const bbbAccountHeading = document.createElement('h3');
      bbbAccountHeading.innerText = 'BBB Account';
      bbbAccountHeading.className = 'bbb-account-heading';
      loggedInSection.appendChild(bbbAccountHeading);

      const accountDivider = document.createElement('div');
      accountDivider.className = 'account-dialog-slim-divider';
      loggedInSection.appendChild(accountDivider);

      const linksSection = document.createElement('div');
      linksSection.className = 'account-links-list';
      loggedInSection.appendChild(linksSection);

      if (this.user.userType === userTypes.business || this.user.userType === userTypes.both) {
        const businessPortalLink = document.createElement('a');
        businessPortalLink.href = `${window.headerFooterData.baseUrl}/account/bid-selector`;
        businessPortalLink.className = 'account-dialog-link';
        businessPortalLink.innerHTML = `
          <span class="account-dialog-link-text">Business Portal</span>
          <span class="account-dialog-link-chevron" aria-hidden="true">${chevronRightIcon}</span>
        `;
        linksSection.appendChild(businessPortalLink);
      }

      if (this.user.userType === userTypes.consumer || this.user.userType === userTypes.both) {
        const reviewsLink = document.createElement('a');
        reviewsLink.href = `${window.headerFooterData.baseUrl}/account/reviews`;
        reviewsLink.className = 'account-dialog-link';
        reviewsLink.innerHTML = `
          <span class="account-dialog-link-text">Reviews</span>
          <span class="account-dialog-link-chevron" aria-hidden="true">${chevronRightIcon}</span>
        `;
        linksSection.appendChild(reviewsLink);

        const complaintsLink = document.createElement('a');
        complaintsLink.href = `${window.headerFooterData.baseUrl}/account/complaints`;
        complaintsLink.className = 'account-dialog-link';
        complaintsLink.innerHTML = `
          <span class="account-dialog-link-text">Complaints</span>
          <span class="account-dialog-link-chevron" aria-hidden="true">${chevronRightIcon}</span>
        `;
        linksSection.appendChild(complaintsLink);

        const scamsLink = document.createElement('a');
        scamsLink.href = `${window.headerFooterData.baseUrl}/account/scams`;
        scamsLink.className = 'account-dialog-link';
        scamsLink.innerHTML = `
          <span class="account-dialog-link-text">Scams</span>
          <span class="account-dialog-link-chevron" aria-hidden="true">${chevronRightIcon}</span>
        `;
        linksSection.appendChild(scamsLink);
      }

      const myAccountLink = document.createElement('a');
      myAccountLink.href = `${window.headerFooterData.baseUrl}/account/home`;
      myAccountLink.className = 'account-dialog-link';
      myAccountLink.innerHTML = `
        <span class="account-dialog-link-text">Profile</span>
        <span class="account-dialog-link-chevron" aria-hidden="true">${chevronRightIcon}</span>
      `;
      linksSection.appendChild(myAccountLink);

      if (this.user.userType === userTypes.consumer) {
        const registerBusinessLink = document.createElement('a');
        registerBusinessLink.className = 'register-account-link';
        registerBusinessLink.innerText = 'Register for a Business Account';
        registerBusinessLink.href = `${window.headerFooterData.baseUrl}/account/register/business`;
        linksSection.appendChild(registerBusinessLink);
      } else if (this.user.userType === userTypes.business) {
        const registerConsumerLink = document.createElement('a');
        registerConsumerLink.className = 'register-account-link';
        registerConsumerLink.innerText = 'Register for a Personal Account';
        registerConsumerLink.href = `${window.headerFooterData.baseUrl}/account/register/personal`;
        linksSection.appendChild(registerConsumerLink);
      }
    } else {
      const notLoggedInSection = document.createElement('div');
      notLoggedInSection.className = 'access-your-account-section';
      dialog.appendChild(notLoggedInSection);

      const accessAccountHeading = document.createElement('h3');
      accessAccountHeading.innerText = 'Access Your BBB Account';
      accessAccountHeading.className = 'access-account-heading';
      notLoggedInSection.appendChild(accessAccountHeading);

      const accessDivider = document.createElement('div');
      accessDivider.className = 'account-dialog-divider';
      notLoggedInSection.appendChild(accessDivider);

      const signInOrRegisterSection = document.createElement('div');
      signInOrRegisterSection.className = 'sign-in-or-register-section';
      notLoggedInSection.appendChild(signInOrRegisterSection);

      const signInBlurb = document.createElement('div');
      signInBlurb.className = 'sign-in-blurb';
      signInBlurb.innerHTML = `<span class="sign-in-emphasis">Sign in</span> or <span class="sign-in-emphasis">register</span> for an <span class="sign-in-emphasis">Account</span> to manage your business or personal interactions with BBB.`;
      signInOrRegisterSection.appendChild(signInBlurb);

      const signInLink = document.createElement('a');
      signInLink.innerText = 'Sign In';
      signInLink.className = 'bds-cta';
      signInLink.setAttribute('data-type', 'featured');
      signInLink.href = `${window.headerFooterData.baseUrl}/account/login`;
      signInOrRegisterSection.appendChild(signInLink);

      const signInDivider = document.createElement('div');
      signInDivider.className = 'account-dialog-divider';
      signInOrRegisterSection.appendChild(signInDivider);

      const registerLink = document.createElement('a');
      registerLink.innerText = 'Register for an account';
      registerLink.className = 'register-link';
      registerLink.href = `${window.headerFooterData.baseUrl}/account/register/personal`;
      signInOrRegisterSection.appendChild(registerLink);
    }

    if (window.headerFooterData?.bbb?.name) {
      const localBbbDivider = document.createElement('div');
      localBbbDivider.className = 'account-dialog-slim-divider';
      dialog.appendChild(localBbbDivider);

      const localBbbSection = document.createElement('div');
      localBbbSection.className = 'account-local-bbb-section';

      const localBbbHeading = document.createElement('h3');
      localBbbHeading.id = 'localBbbHeading';
      localBbbHeading.innerText = 'Local BBB';
      localBbbSection.appendChild(localBbbHeading);

      const houseBuildingIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" aria-hidden="true" focusable="false" width="1em" height="1em" fill="currentColor"><path d="M368 0c-26.5 0-48 21.5-48 48V153.9l64 58.7V208c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H413.2c1.9 6.8 2.8 13.9 2.8 21.1V288h16c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H416V464c0 18-6 34.6-16 48H592c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48H368zM512 208c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H528c-8.8 0-16-7.2-16-16V208zm16 80h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H528c-8.8 0-16-7.2-16-16V304c0-8.8 7.2-16 16-16zM400 96h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H400c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16zm112 16c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H528c-8.8 0-16-7.2-16-16V112zM0 277.1V464c0 26.5 21.5 48 48 48H336c26.5 0 48-21.5 48-48V277.1c0-13.5-5.6-26.3-15.6-35.4l-144-132c-18.4-16.8-46.5-16.8-64.9 0l-144 132C5.6 250.8 0 263.7 0 277.1zM144 296c0-13.3 10.7-24 24-24h48c13.3 0 24 10.7 24 24v48c0 13.3-10.7 24-24 24H168c-13.3 0-24-10.7-24-24V296z"/></svg>`;

      const localBbbCard = document.createElement('div');
      localBbbCard.className = 'account-local-bbb-card';

      const localBbbLink = document.createElement('a');
      localBbbLink.className = 'account-local-bbb-link';
      localBbbLink.href = window.headerFooterData?.bbb?.url || '/bbb-directory';
      localBbbLink.innerHTML = `
        <span class="account-local-bbb-label">
          <span class="account-local-bbb-icon" aria-hidden="true">${houseBuildingIcon}</span>
          <span class="account-local-bbb-text">
            <span class="account-local-bbb-name">${window.headerFooterData.bbb.name}</span>
          </span>
        </span>
        <span class="account-local-bbb-chevron" aria-hidden="true">${chevronRightIcon}</span>
      `;

      const changeLink = document.createElement('a');
      changeLink.href = `${window.headerFooterData.baseUrl}/manage-location`;
      changeLink.id = 'changeBbbLink';
      changeLink.className = 'change-bbb-link';
      changeLink.setAttribute('aria-labelledby', 'changeBbbLink localBbbHeading');
      changeLink.innerText = 'Change';

      localBbbCard.appendChild(localBbbLink);
      localBbbCard.appendChild(changeLink);

      localBbbSection.appendChild(localBbbCard);
      dialog.appendChild(localBbbSection);
    }

    if (this.user) {
      const signOutButton = document.createElement('a');
      signOutButton.className = 'sign-out-button-redesign';
      signOutButton.innerText = 'Sign Out';
      const signOutReturnUrl = window.location.pathname.startsWith('/account')
        ? `${window.headerFooterData.baseUrl}/account/login`
        : window.location.href;
      signOutButton.href = `${window.headerFooterData.baseUrl}/account/logout?returnUrl=${encodeURIComponent(signOutReturnUrl)}`;
      dialog.appendChild(signOutButton);
    }

    document.body.appendChild(dialog);
    this.updateDialogPosition(dialog);
    dialog.showModal();

    // Close on backdrop click
    dialog.addEventListener('click', (e) => {
      const dialogDimensions = dialog.getBoundingClientRect();
      if (
        e.clientX < dialogDimensions.left ||
        e.clientX > dialogDimensions.right ||
        e.clientY < dialogDimensions.top ||
        e.clientY > dialogDimensions.bottom
      ) {
        dialog.close();
        this.destroyRedesignDialog();
      }
    });

    // Close on Escape key (handled automatically by dialog element)
    dialog.addEventListener('close', () => {
      if (document.querySelector('.account-dialog-redesign')) {
        this.destroyRedesignDialog();
      }
      this.querySelector('button').focus();
    });

    window.addEventListener('resize', this.handleRedesignDialogPositionOnResize, { passive: true });
  };

  handleLegacyDialogPositionOnResize = () => {
    const dialogToListen = document.querySelector('.account-dialog');
    this.updateDialogPosition(dialogToListen);
  };

  handleRedesignDialogPositionOnResize = () => {
    const dialogToListen = document.querySelector('.account-dialog-redesign');
    this.updateDialogPosition(dialogToListen);
  };

  updateDialogPosition = (dialog) => {
    const buttonBoundingClientRect = this.querySelector('button').getBoundingClientRect();
    if (!dialog || buttonBoundingClientRect.left === 0) {
      return;
    }
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    if (vw < 768) {
      // 48em, Cutoff for mobile design. ignore the default value for left (40px)
      dialog.style.left = 'revert';
    } else {
      const desktopDialogWidth = this.user ? 300 : 500;
      dialog.style.left = `${buttonBoundingClientRect.right - desktopDialogWidth}px`;
    }
    dialog.style.top = `${buttonBoundingClientRect.top}px`;
  };

  destroyRedesignDialog = () => {
    const dialog = document.querySelector('.account-dialog-redesign');
    if (dialog) {
      dialog.remove();
    }

    window.removeEventListener('resize', this.handleRedesignDialogPositionOnResize);
  };

  /**
   * Emit a custom event
   * @param {string} name - The event name
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (name, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  setButtonOnClick = () => {
    const btn = this.querySelector('button');
    btn.onclick = () => {
      this.buildAndOpenRedesignDialog();
    };
  };

  updateButtonLabel = () => {
    const label = this.querySelector('.my-account-label');
    if (!label) {
      return;
    }
    label.textContent = this.user ? 'My account' : 'Sign in';
  };
}

customElements.define('iabbb-my-account', MyAccount);
